// Constant-velocity Kalman filters for GPS (east/north) and compass/pitch,
// plus a random-walk channel for altitude. Predict with the motion model,
// then correct with the latest measurement — the same structure used to
// fuse odometry and GPS when wheels slip.

const EARTH_M = 6_371_000
const STILL_MPS = 0.45
// Altitude channel: vertical GPS is sparse and biased, so model floor
// changes as a slow random walk instead of a velocity state (a velocity
// state would invent fake climbs from noisy readings).
const ALT_Q_M2_S = 0.75 // process noise, m^2 per second of real time
const ALT_SIGMA_MIN_M = 8 // never trust a reading tighter than this
const ALT_GATE_SIGMA = 3 // reject innovations beyond this (multipath spikes)
const ALT_DT_MAX_S = 30 // cap sample interval (tab sleep, long gaps)

function ident(n) {
  const out = Array.from({length: n}, () => Array(n).fill(0))
  for (let i = 0; i < n; i++) out[i][i] = 1
  return out
}

function matMul(a, b) {
  const n = a.length
  const m = b[0].length
  const p = b.length
  const out = Array.from({length: n}, () => Array(m).fill(0))
  for (let i = 0; i < n; i++) {
    for (let k = 0; k < p; k++) {
      const aik = a[i][k]
      if (aik === 0) continue
      for (let j = 0; j < m; j++) out[i][j] += aik * b[k][j]
    }
  }
  return out
}

function matT(a) {
  const n = a.length
  const m = a[0].length
  const out = Array.from({length: m}, () => Array(n).fill(0))
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < m; j++) out[j][i] = a[i][j]
  }
  return out
}

function matAdd(a, b) {
  return a.map((row, i) => row.map((v, j) => v + b[i][j]))
}

function matVec(a, v) {
  return a.map((row) => row.reduce((sum, x, j) => sum + x * v[j], 0))
}

function wrapPi(rad) {
  let x = rad
  while (x > Math.PI) x -= 2 * Math.PI
  while (x < -Math.PI) x += 2 * Math.PI
  return x
}

function predictCv(state, P, dt, qAccel, velTau) {
  const damp = Math.exp(-dt / velTau)
  const f = ident(4)
  f[0][2] = dt
  f[1][3] = dt
  f[2][2] = damp
  f[3][3] = damp

  const next = matVec(f, state)
  const dt2 = dt * dt
  const dt3 = dt2 * dt
  const dt4 = dt2 * dt2
  const q = [
    [qAccel * dt4 * 0.25, 0, qAccel * dt3 * 0.5, 0],
    [0, qAccel * dt4 * 0.25, 0, qAccel * dt3 * 0.5],
    [qAccel * dt3 * 0.5, 0, qAccel * dt2, 0],
    [0, qAccel * dt3 * 0.5, 0, qAccel * dt2],
  ]
  const pPred = matAdd(matMul(matMul(f, P), matT(f)), q)
  return {state: next, P: pPred}
}

function correctPosition(state, P, eMeas, nMeas, sigma) {
  const r = Math.max(sigma, 4) ** 2
  const h = [
    [1, 0, 0, 0],
    [0, 1, 0, 0],
  ]
  const z = [eMeas, nMeas]
  const y = [z[0] - state[0], z[1] - state[1]]
  const ht = matT(h)
  const s = matAdd(matMul(matMul(h, P), ht), [
    [r, 0],
    [0, r],
  ])
  const det = s[0][0] * s[1][1] - s[0][1] * s[1][0]
  if (Math.abs(det) < 1e-12) return {state, P}

  const sinv = [
    [s[1][1] / det, -s[0][1] / det],
    [-s[1][0] / det, s[0][0] / det],
  ]
  const k = matMul(matMul(P, ht), sinv)
  const next = state.map((v, i) => v + k[i][0] * y[0] + k[i][1] * y[1])
  const i4 = ident(4)
  const kh = matMul(k, h)
  const iKh = i4.map((row, i) => row.map((v, j) => v - kh[i][j]))
  const pNext = matMul(iKh, P)
  return {state: next, P: pNext}
}

function predictAngle(theta, omega, P, dt, qAccel, velTau) {
  const damp = Math.exp(-dt / velTau)
  const f = [
    [1, dt],
    [0, damp],
  ]
  const nextTheta = wrapPi(theta + omega * dt)
  const nextOmega = omega * damp
  const q = [
    [qAccel * dt * dt * dt * dt * 0.25, qAccel * dt * dt * dt * 0.5],
    [qAccel * dt * dt * dt * 0.5, qAccel * dt * dt],
  ]
  const pPred = matAdd(matMul(matMul(f, P), matT(f)), q)
  return {theta: nextTheta, omega: nextOmega, P: pPred}
}

function correctAngle(theta, omega, P, meas, sigma, wrap) {
  const r = Math.max(sigma, 0.5) ** 2
  const h = [1, 0]
  const y = wrap ? wrapPi(meas - theta) : meas - theta
  const s = P[0][0] + r
  if (s < 1e-12) return {theta, omega, P}
  const k0 = P[0][0] / s
  const k1 = P[1][0] / s
  const nextTheta = wrap ? wrapPi(theta + k0 * y) : theta + k0 * y
  const nextOmega = omega + k1 * y
  const p00 = (1 - k0) * P[0][0]
  const p01 = (1 - k0) * P[0][1]
  const p10 = P[1][0] - k1 * P[0][0]
  const p11 = P[1][1] - k1 * P[0][1]
  return {theta: nextTheta, omega: nextOmega, P: [[p00, p01], [p10, p11]]}
}

export function createPoseFilter(clock = () => performance.now()) {
  let origin = null
  let lastT = null
  let pos = [0, 0, 0, 0]
  let posP = ident(4).map((row, i) => row.map((v, j) => (i === j ? (i < 2 ? 400 : 25) : 0)))
  let heading = {theta: 0, omega: 0, P: ident(2), ready: false}
  let pitch = {theta: 0, omega: 0, P: ident(2), ready: false}
  let haveGps = false
  let gyroUp = null
  let alt = {value: 0, p: 0, ready: false}
  let altT = null

  function elapsed() {
    const now = clock() / 1000
    if (lastT == null) {
      lastT = now
      return 0
    }
    let dt = now - lastT
    lastT = now
    if (dt < 0) dt = 0
    if (dt > 0.25) dt = 0.25
    return dt
  }

  function still() {
    const speed = Math.hypot(pos[2], pos[3])
    if (speed < STILL_MPS) return true
    const posSigma = Math.sqrt(Math.max(posP[0][0], posP[1][1], 0))
    // Large GPS uncertainty invents fake velocity. Do not treat that as walking.
    return posSigma > 5 && speed < 1.15
  }

  function advance(dt) {
    if (!(dt > 0)) return
    if (haveGps) {
      const q = still() ? 0.15 : 2.2
      const tau = still() ? 0.35 : 2.5
      const next = predictCv(pos, posP, dt, q, tau)
      pos = next.state
      posP = next.P
    }
    if (heading.ready) {
      const turning = !still() && Number.isFinite(gyroUp)
      const rate = turning ? gyroUp : heading.omega
      const next = predictAngle(heading.theta, rate, heading.P, dt, still() ? 0.4 : 2.5, 0.18)
      heading = {
        ...heading,
        ...next,
        omega: turning ? gyroUp : next.omega,
      }
    }
    if (pitch.ready) {
      const next = predictAngle(pitch.theta, pitch.omega, pitch.P, dt, 1.2, 0.12)
      pitch = {...pitch, ...next, theta: next.theta, ready: true}
    }
  }

  function predict() {
    advance(elapsed())
  }

  function setGyro(omegaUpRadPerSec) {
    gyroUp = Number.isFinite(omegaUpRadPerSec) ? omegaUpRadPerSec : null
  }

  function gpsLag(timestampMs) {
    if (!Number.isFinite(timestampMs)) return 0
    const lag = (Date.now() - timestampMs) / 1000
    if (lag < 0) return 0
    if (lag > 0.5) return 0.5
    return lag
  }

  function latLngToEn(lat, lng) {
    const lat0 = (origin.lat * Math.PI) / 180
    const east = EARTH_M * Math.cos(lat0) * (((lng - origin.lng) * Math.PI) / 180)
    const north = EARTH_M * (((lat - origin.lat) * Math.PI) / 180)
    return [east, north]
  }

  function enToLatLng(east, north) {
    const lat0 = (origin.lat * Math.PI) / 180
    return {
      lat: origin.lat + (north / EARTH_M) * (180 / Math.PI),
      lng: origin.lng + (east / (EARTH_M * Math.cos(lat0))) * (180 / Math.PI),
    }
  }

  function seed(lat, lng, accuracy) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    origin = {lat, lng}
    pos = [0, 0, 0, 0]
    const sigma = Math.max(Number(accuracy) || 40, 8)
    posP = ident(4).map((row, i) =>
      row.map((v, j) => (i === j ? (i < 2 ? sigma * sigma : 25) : 0))
    )
    haveGps = true
    lastT = performance.now() / 1000
  }

  function updateGps(lat, lng, accuracy, timestampMs) {
    predict()
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    if (!origin) {
      origin = {lat, lng}
      pos = [0, 0, 0, 0]
      haveGps = true
    }
    const [east, north] = latLngToEn(lat, lng)
    let sigma = Number.isFinite(accuracy) ? accuracy : 15
    if (still()) sigma = Math.max(sigma * 3, 18)
    const innov = Math.hypot(east - pos[0], north - pos[1])
    if (still() && innov > Math.max(2.4 * sigma, 12)) {
      haveGps = true
      return
    }
    const next = correctPosition(pos, posP, east, north, sigma)
    pos = next.state
    posP = next.P
    haveGps = true
    advance(gpsLag(timestampMs))
  }

  function updateHeading(deg, accuracyDeg) {
    predict()
    if (!Number.isFinite(deg)) return
    const meas = wrapPi((deg * Math.PI) / 180)
    const sigma = Number.isFinite(accuracyDeg) && accuracyDeg > 0
      ? (Math.min(accuracyDeg, 75) * Math.PI) / 180
      : (12 * Math.PI) / 180
    if (!heading.ready) {
      heading = {theta: meas, omega: 0, P: ident(2), ready: true}
      return
    }
    heading = {
      ...heading,
      ...correctAngle(heading.theta, heading.omega, heading.P, meas, sigma, true),
      ready: true,
    }
  }

  function updatePitch(deg) {
    predict()
    if (!Number.isFinite(deg)) return
    const meas = (deg * Math.PI) / 180
    if (!pitch.ready) {
      pitch = {theta: meas, omega: 0, P: ident(2), ready: true}
      return
    }
    pitch = {
      ...pitch,
      ...correctAngle(pitch.theta, pitch.omega, pitch.P, meas, (4 * Math.PI) / 180, false),
      ready: true,
    }
  }

  // One-dimensional Kalman filter on the phone's WGS84 altitude.
  // Process model: random walk (fans are mostly at one floor level).
  // Observation: the GPS reading, variance from the reported accuracy.
  // Innovations beyond ALT_GATE_SIGMA are dropped: vertical multipath in a
  // stadium is a biased spike, not a Gaussian blip.
  function updateAltitude(meas, accuracy, timestampMs) {
    const t = Number.isFinite(timestampMs) ? timestampMs / 1000 : clock() / 1000
    if (alt.ready) {
      let dt = t - (altT ?? t)
      if (dt < 0) dt = 0
      if (dt > ALT_DT_MAX_S) dt = ALT_DT_MAX_S
      alt.p += ALT_Q_M2_S * dt
    }
    altT = t
    if (!Number.isFinite(meas)) return
    const sigma = Number.isFinite(accuracy) && accuracy > 0 ? accuracy : 15
    if (!alt.ready) {
      alt = {value: meas, p: Math.max(sigma, ALT_SIGMA_MIN_M) ** 2, ready: true}
      return
    }
    const r = Math.max(sigma, ALT_SIGMA_MIN_M) ** 2
    const y = meas - alt.value
    const s = alt.p + r
    if (Math.abs(y) > ALT_GATE_SIGMA * Math.sqrt(s)) return
    const k = alt.p / s
    alt = {value: alt.value + k * y, p: (1 - k) * alt.p, ready: true}
  }

  function altitudeEstimate() {
    if (!alt.ready) return null
    return {value: alt.value, sigma: Math.sqrt(alt.p)}
  }

  function snapshot() {
    if (!haveGps || !origin) return null
    const {lat, lng} = enToLatLng(pos[0], pos[1])
    return {
      lat,
      lng,
      speedMps: Math.hypot(pos[2], pos[3]),
      heading: heading.ready ? ((heading.theta * 180) / Math.PI + 360) % 360 : null,
      pitch: pitch.ready ? (pitch.theta * 180) / Math.PI : 0,
    }
  }

  return {
    predict,
    seed,
    updateGps,
    updateHeading,
    updatePitch,
    setGyro,
    updateAltitude,
    altitudeEstimate,
    snapshot,
  }
}
